import { ANALYTICS_EVENTS, POSTED_PRICE_TABLE, UC_TIERS } from '@syncanix/shared';

import { track } from '@/analytics/track';
import { ctaClasses } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { annualMonthlyUsd, formatUsd } from '@/features/pricing-card/billing-period';
import { formatLocaleNumber } from '@/i18n/format';
import { salesContactHref } from '@/lib/site-url';

import { recommendTier } from './uc-estimate';

import type { MixEstimate } from './uc-estimate';
import type { ReactElement } from 'react';

/**
 * The calculator's ANSWER panel, hoisted out of `UcCalculator.tsx` (over the
 * ~300-line ceiling in `.claude/rules/architecture.md`). The cut is the seam
 * the file already drew for itself — a "Controls" half and a "Result" half,
 * each already marked with its own JSX comment — and this side is a leaf: it
 * takes the computed estimate as a prop and imports nothing back from its
 * parent.
 *
 * It carries the brand rule across its top edge (the `Panel accent` device) so
 * the answer is distinguishable from the controls at a glance rather than by
 * reading both, and the usage-credit total LEADS it. That total used to sit at
 * the bottom of the controls card in 14px grey, which put the question and its
 * answer in different boxes and left this panel with ~300px of empty space
 * under a short plan blurb.
 */
export function UcResultPanel({ estimate }: { readonly estimate: MixEstimate }): ReactElement {
  const { t, locale } = useT();
  const usageCredits = estimate.usageCredits;
  const tier = recommendTier(usageCredits);
  const meta = UC_TIERS[tier];
  const isEnterprise = tier === 'enterprise';
  const monthly = meta.monthlyPriceUsd;

  const fmtCount = (n: number): string => formatLocaleNumber(n, locale);

  return (
    <div
      data-recommended-tier={tier}
      aria-live="polite"
      className="relative flex flex-col overflow-hidden rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)] p-6 pt-7 sm:p-8 sm:pt-9"
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: 'var(--bd-gradient)' }}
      />

      {/* The label lost its trailing colon in all six locales when it moved
          here: it was punctuated for the inline prose it used to sit in
          ("Usage credits: 26,250 UC / mo"), and a colon over a standalone
          figure reads as a truncated sentence. */}
      <p className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
        {t('pricing.calculator.estimatedUc.label')}
      </p>
      <p className="mt-2 flex items-baseline gap-2">
        <span
          data-estimated-uc
          className="text-[2.6rem] font-semibold leading-none tracking-[-0.045em] tabular-nums text-[color:var(--bd-ink)]"
        >
          {fmtCount(usageCredits)}
        </span>
        <span className="text-base text-[color:var(--bd-muted)]">
          {t('pricing.calculator.ucUnit')}
        </span>
      </p>
      <p
        data-uc-breakdown
        className="mt-2.5 text-pretty text-[0.82rem] leading-[1.55] text-[color:var(--bd-muted)]"
      >
        {/* Four CELLS, not a base plus two increments: under ADR-0776 the menu
            is a table, so the only breakdown that reconstructs the total is the
            one that names how many interactions landed in each cell. The prices
            are interpolated from the shared menu rather than written into the
            sentence, so the arithmetic a buyer checks is the arithmetic the
            meter runs. */}
        {t('pricing.calculator.ucHelp', {
          answer: fmtCount(estimate.cells.answer),
          answerUc: fmtCount(POSTED_PRICE_TABLE.answer),
          acted: fmtCount(estimate.cells.acted),
          actedUc: fmtCount(POSTED_PRICE_TABLE.acted),
          lookedUp: fmtCount(estimate.cells.lookedUp),
          lookedUpUc: fmtCount(POSTED_PRICE_TABLE.lookedUp),
          both: fmtCount(estimate.cells.both),
          bothUc: fmtCount(POSTED_PRICE_TABLE.both),
        })}
      </p>

      <p className="mt-7 border-t border-[color:var(--bd-line)] pt-6 font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
        {t('pricing.calculator.recommended.label')}
      </p>

      {isEnterprise ? (
        <>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]">
            {meta.displayName}
          </p>
          <p className="mt-3 text-sm leading-[1.62] text-[color:var(--bd-body)]">
            {t('pricing.calculator.enterprise.desc')}
          </p>
          <div className="mt-auto pt-6">
            <Link to={salesContactHref()} className={ctaClasses({ size: 'sm' })}>
              {t('pricing.calculator.enterprise.cta')}
            </Link>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)]">
              {meta.displayName}
            </span>
            {monthly !== null ? (
              <span className="text-lg font-medium tabular-nums text-[color:var(--bd-ink)]">
                {formatUsd(monthly)}
                <span className="text-sm font-normal text-[color:var(--bd-muted)]">
                  {t('pricing.period.monthly')}
                </span>
              </span>
            ) : null}
          </p>
          {monthly !== null ? (
            <p className="mt-2 text-sm text-[color:var(--bd-muted)]">
              {t('pricing.calculator.annualNote', {
                price: formatUsd(annualMonthlyUsd(monthly)),
              })}
            </p>
          ) : null}
          <p className="mt-3 text-sm text-[color:var(--bd-muted)]">
            {t('pricing.calculator.included', { uc: fmtCount(meta.includedUsageCredits) })}
          </p>
          <p className="mt-3 border-t border-[color:var(--bd-line)] pt-4 text-sm leading-[1.62] text-[color:var(--bd-body)]">
            {t('pricing.calculator.overage')}
          </p>
          <div className="mt-auto pt-6">
            <Link
              to={salesContactHref()}
              className={ctaClasses({ size: 'sm' })}
              onClick={() => {
                // A sales click, not a sign-up: this CTA has routed to /contact
                // since the 2026-07-16 sales-led reroute — the old event name
                // mislabelled it in the funnel.
                track(ANALYTICS_EVENTS.MARKETING_CONTACT_CTA_CLICKED, {
                  location: 'uc-calculator',
                });
              }}
            >
              {t('pricing.calculator.recommended.cta')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
